
import { useState, useEffect } from "react";
import { BuyerProfile, SavedProperty, SavedSearch } from "@/types/buyer";
import { buyerService } from "@/services/buyerService";
import { toast } from "@/hooks/use-toast";

export const useBuyerData = (userId: string | null) => {
  const [buyerProfile, setBuyerProfile] = useState<BuyerProfile | null>(null);
  const [savedProperties, setSavedProperties] = useState<SavedProperty[]>([]);
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [isLoading, setIsLoading] = useState(true); // Start with loading true
  const [hasInitialized, setHasInitialized] = useState(false);

  // Fetch buyer profile
  const fetchBuyerProfile = async (userId: string) => {
    try {
      setIsLoading(true);
      const profile = await buyerService.fetchBuyerProfile(userId);
      setBuyerProfile(profile);
      return profile;
    } catch (error) {
      console.error("Error fetching buyer profile:", error);
      setBuyerProfile(null);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch saved properties
  const fetchSavedProperties = async (buyerId: string) => {
    const properties = await buyerService.fetchSavedProperties(buyerId);
    setSavedProperties(properties);
  };

  // Fetch saved searches
  const fetchSavedSearches = async (buyerId: string) => {
    const searches = await buyerService.fetchSavedSearches(buyerId);
    setSavedSearches(searches);
  };

  // Save property
  const saveProperty = async (propertyId: string) => {
    if (!buyerProfile) return;

    const result = await buyerService.saveProperty(buyerProfile.id, propertyId);
    
    if (result.success) {
      await fetchSavedProperties(buyerProfile.id);
      toast({
        title: "Property saved",
        description: "Property has been added to your saved list.",
      });
    } else {
      toast({
        title: result.error === "This property is already in your saved list." ? "Already saved" : "Error saving property",
        description: result.error,
        variant: result.error === "This property is already in your saved list." ? "default" : "destructive",
      });
    }
  };

  // Unsave property
  const unsaveProperty = async (propertyId: string) => {
    if (!buyerProfile) return;

    const result = await buyerService.unsaveProperty(buyerProfile.id, propertyId);
    
    if (result.success) {
      await fetchSavedProperties(buyerProfile.id);
      toast({
        title: "Property removed",
        description: "Property has been removed from your saved list.",
      });
    } else {
      toast({
        title: "Error removing property",
        description: result.error,
        variant: "destructive",
      });
    }
  };

  // Check if property is saved
  const isPropertySaved = (propertyId: string) => {
    return savedProperties.some(sp => sp.property_id === propertyId);
  };

  // Save search
  const saveSearch = async (name: string, criteria: any) => {
    if (!buyerProfile) return;

    const result = await buyerService.saveSearch(buyerProfile.id, name, criteria);
    
    if (result.success) {
      await fetchSavedSearches(buyerProfile.id);
      toast({
        title: "Search saved",
        description: `Search "${name}" has been saved.`,
      });
    } else {
      toast({
        title: result.error?.includes('8 searches') ? "Search limit reached" : "Error saving search",
        description: result.error,
        variant: "destructive",
      });
    }
  };

  // Delete search
  const deleteSearch = async (searchId: string) => {
    const result = await buyerService.deleteSearch(searchId);
    
    if (result.success) {
      if (buyerProfile) {
        await fetchSavedSearches(buyerProfile.id);
      }
      toast({
        title: "Search deleted",
        description: "Saved search has been deleted.",
      });
    } else {
      toast({
        title: "Error deleting search",
        description: result.error,
        variant: "destructive",
      });
    }
  };

  // Update buyer profile
  const updateBuyerProfile = async (updates: Partial<BuyerProfile>) => {
    if (!buyerProfile) return;

    setIsLoading(true);
    console.log("Updating buyer profile with updates:", updates);
    
    const result = await buyerService.updateBuyerProfile(buyerProfile.id, updates);
    
    if (result.success) {
      // Refresh the buyer profile from the database to get the latest data
      const updatedProfile = await fetchBuyerProfile(buyerProfile.user_id);
      console.log("Profile refreshed after update:", updatedProfile);
      
      toast({
        title: "Profile updated",
        description: "Your profile has been updated successfully.",
      });
    } else {
      console.error("Failed to update profile:", result.error);
      toast({
        title: "Error updating profile",
        description: result.error,
        variant: "destructive",
      });
    }
    setIsLoading(false);
  };

  // Refresh all buyer data
  const refreshBuyerData = async () => {
    if (!userId || !buyerProfile) return;

    setIsLoading(true);
    try {
      await Promise.all([
        fetchBuyerProfile(userId),
        fetchSavedProperties(buyerProfile.id),
        fetchSavedSearches(buyerProfile.id)
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize buyer data when user changes
  useEffect(() => {
    if (userId) {
      console.log("Fetching buyer profile for user:", userId);
      fetchBuyerProfile(userId).finally(() => setHasInitialized(true));
    } else {
      console.log("No userId, clearing buyer data");
      setBuyerProfile(null);
      setSavedProperties([]);
      setSavedSearches([]);
      setIsLoading(false);
      setHasInitialized(true);
    }
  }, [userId]);

  // Fetch related data when buyer profile is loaded
  useEffect(() => {
    if (buyerProfile) {
      console.log("Buyer profile loaded, fetching related data");
      fetchSavedProperties(buyerProfile.id);
      fetchSavedSearches(buyerProfile.id);
    }
  }, [buyerProfile]);

  return {
    buyerProfile,
    savedProperties,
    savedSearches,
    isLoading: isLoading || !hasInitialized,
    saveProperty,
    unsaveProperty,
    isPropertySaved,
    saveSearch,
    deleteSearch,
    updateBuyerProfile,
    refreshBuyerData
  };
};
